<template lang="pug">
yz-dialog(v-model="showDialog" persistent scrollable max-width="640px")
  yz-card
    yz-dialog-normal-header(title="Assign inventory to branch & warehouse" @close="onCloseDialog")
    yz-dialog-normal-content.px-6.py-6
      //- Description
      .yz-heading-8.text-neutral-700 Assign to specific branch & warehouse
      .yz-regular-body-sm.text-neutral-500.mt-1 Choose your preferred branch and warehouse for tracking and stock control.

      //- Branch section
      yz-card.mt-4(v-if="branches.length" outlined)
        .pa-4
          .yz-heading-9.text-neutral-700 Branch
        yz-divider.my-0
        .pa-4(v-for="branch in branches" :key="branch.id")
          .d-flex.align-center.justify-space-between
            div
              .yz-medium-body-sm.text-neutral-700 {{ branch.name }}
              .yz-regular-body-xs.text-neutral-500(v-if="branch.address") {{ branch.address }}
            yz-switch(:value="isAssigned(branch.id)" size="md" @input="onToggleAssignment(branch.id, $event)")

      //- Warehouse section
      yz-card.mt-4(v-if="warehouses.length" outlined)
        .pa-4
          .yz-heading-9.text-neutral-700 Warehouse
        yz-divider.my-0
        .pa-4(v-for="warehouse in warehouses" :key="warehouse.id")
          .d-flex.align-center.justify-space-between
            div
              .yz-medium-body-sm.text-neutral-700 {{ warehouse.name }}
              .yz-regular-body-xs.text-neutral-500(v-if="warehouse.address") {{ warehouse.address }}
            yz-switch(:value="isAssigned(warehouse.id)" size="md" @input="onToggleAssignment(warehouse.id, $event)")

      //- Loading state
      .d-flex.justify-center.pa-8(v-if="isLoading")
        v-progress-circular(indeterminate color="primary")

    yz-dialog-normal-actions(save-label="Confirm" cancel-label="Cancel" :loading="isSaving" :disable-save="isLoading" @save="onConfirm" @cancel="onCloseDialog")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    open: { type: Boolean, default: false },
    items: { type: Array, default: () => [] }
  },
  data () {
    return {
      isLoading: false,
      isSaving: false,
      allLocations: [],
      assignmentMap: {},
      initialAssignmentMap: {}
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    showDialog: {
      get () { return this.open },
      set (newValue) { this.$emit('update:open', newValue) }
    },
    companyId () {
      return this.company.branch_of?.id || this.company.id
    },
    branches () {
      return this.allLocations.filter(l => l.type === 'branch')
    },
    warehouses () {
      return this.allLocations.filter(l => l.type === 'warehouse')
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    ...mapActions(['showMessage']),
    isAssigned (locationId) {
      return !!this.assignmentMap[locationId]
    },
    onToggleAssignment (locationId, value) {
      this.$set(this.assignmentMap, locationId, value)
    },
    async fetchData () {
      this.isLoading = true
      try {
        // Fetch all locations
        const locResp = await this.$axios.get(`/companies/${this.companyId}/branches`, { params: { page_size: -1 } })
        this.allLocations = (locResp.data.results || []).map(l => ({
          id: l.id,
          name: l.name || l.company_name,
          address: this.formatAddress(l.address),
          type: l.os === 'warehouse' ? 'warehouse' : 'branch'
        }))
        // Fetch assigned branches for each selected item
        const assignedSets = await Promise.all(
          this.items.map(item =>
            this.$axios.get(`/clinics/${this.company.slug}/hq/medications/${item.id}/branches`, { params: { assigned: true, page_size: -1 } })
              .then(resp => new Set((resp.data.results || []).map(b => b.id)))
              .catch(() => new Set())
          )
        )
        // A location is toggled ON only if ALL selected items are assigned to it
        const map = {}
        this.allLocations.forEach(loc => {
          map[loc.id] = assignedSets.every(set => set.has(loc.id))
        })
        this.assignmentMap = map
        this.initialAssignmentMap = { ...map }
      } catch {
        this.showMessage(['error', 'Failed to fetch locations'])
      } finally {
        this.isLoading = false
      }
    },
    async onConfirm () {
      // Compute diff: which locations changed
      const toAssign = []
      const toUnassign = []
      Object.keys(this.assignmentMap).forEach(locId => {
        const now = this.assignmentMap[locId]
        const was = this.initialAssignmentMap[locId]
        if (now && !was) toAssign.push(Number(locId))
        if (!now && was) toUnassign.push(Number(locId))
      })
      if (toAssign.length === 0 && toUnassign.length === 0) {
        this.onCloseDialog()
        return
      }
      this.isSaving = true
      try {
        // Send PATCH per selected medication item
        const requests = this.items.map(item => {
          const branches = [
            ...toUnassign.map(id => ({ company: id, deleted: true })),
            ...toAssign.map(id => ({ company: id, inventory_qty: 0, location: null }))
          ]
          return this.$axios.patch(`/clinics/${this.company.slug}/hq/medications/${item.id}`, { branches })
        })
        await Promise.all(requests)
        this.showMessage(['success', 'Branches & warehouses updated successfully'])
        this.$emit('updated')
        this.onCloseDialog()
      } catch {
        this.showMessage(['error', 'Failed to update branch assignments'])
      } finally {
        this.isSaving = false
      }
    },
    formatAddress (address) {
      if (!address) return null
      const parts = [address.address_1, address.address_2, address.postcode, address.city, address.state].filter(Boolean)
      return parts.join(', ')
    },
    onCloseDialog () {
      this.$emit('update:open', false)
      this.$emit('closed')
    }
  }
}
</script>
